import React from 'react';
import { Provider } from 'react-redux';
import { createRoot } from 'react-dom/client';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import WorldInterface from 'utils/world-interface';
import App from 'app';
import LayoutManager from 'utils/layout-manager';
import MuiThemeCustomizer from 'utils/mui-theme-customizer';
import LocalEvent from 'utils/local-event';
import sdkClient from 'utils/sdk-client';
import store from 'modules/shared/stores/store';
import { injectEnvironmentLocales } from 'utils/translate';
import Log from 'utils/log';
import Queue from 'utils/queue';
import { companySettingsSliceHandler } from 'modules/settings/stores/company-settings-slice-handler';
import ConfigIniUtil from './utils/config-ini-util';
import { userSettingsSliceHandler } from './modules/settings/stores/user-settings-slice-handler';

// publish the interface in window's context
window.WCFUI = WorldInterface;

const rootId = 'wcf-ui-root';

window.addEventListener('load', () => {
    // make sure that the code does not break when the version is not propely configured
    if (typeof STWV === 'undefined') {
        window.STWV = '0.0.0';
    }

    let root = document.getElementById(rootId);

    // make sure that we start clean
    if (root !== null) {
        root.innerHTML = '';
    } else {
        root = document.createElement('div');
        root.setAttribute('id', rootId);
        document.body.prepend(root);
    }

    const reactRoot = createRoot(document.getElementById(rootId));

    const renderApp = () => {
        const theme = MuiThemeCustomizer.getCustomizedTheme();

        injectEnvironmentLocales();

        reactRoot.render(
            <Provider store={store}>
                <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <App />
                </ThemeProvider>
            </Provider>,
        );
    };

    const initializationQueue = new Queue(() => renderApp());

    initializationQueue.add(() => ConfigIniUtil.loadData().then(() => initializationQueue.advance()));

    initializationQueue.add(() => {
        sdkClient.init(rootId).then((isGuest) => {
            if (!isGuest) {
                Promise.all([
                    userSettingsSliceHandler.loadUserSettingsJson(),
                    companySettingsSliceHandler.loadCoreConfigurationSettings(),
                ])
                    .then(() => initializationQueue.advance())
                    .catch(() => {
                        Log.error(
                            'INDEX',
                            'There was an error while loading the user settings json & CORE configuration settings.',
                        );
                    });
            } else {
                initializationQueue.advance();
            }
        });
    });

    initializationQueue.add(() => LayoutManager.setupEnvironment().then(() => initializationQueue.advance()));

    initializationQueue.run();

    // when the language has been changed entire app must re-render
    LocalEvent.setListener(LocalEvent.eventTypes.LAYOUT_MANAGER_LANGUAGE_UPDATED, () => {
        renderApp();
    });
});
